import {
  MagnifyingGlassIcon,
  PlusIcon
} from '@heroicons/react/24/outline'
import {
  Button,
  Card,
  Chip,
  Input,
  Select,
  SelectItem,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow
} from '@heroui/react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { useSnapshot } from 'valtio'
import { api } from '@/lib/api'
import { getLatestRelease } from '@/lib/data'
import type { Item, LicenseCheckResponse } from '../../../../types'
import { state } from '../../lib/state'

// Helper function to calculate a hash for items array to detect changes
const calculateItemsHash = (items: Item[]): string => {
  return items
    .map(item => {
      // Create a signature that captures the essential data
      const latestRelease =
        item.releases[0] ||
        ({} as {
          title?: string
          releaseDate?: string
          ring?: string
          flag?: string
        })
      return `${item.id}-${item.archived}-${latestRelease.title || ''}-${latestRelease.releaseDate || ''}-${latestRelease.ring || ''}-${latestRelease.flag || ''}`
    })
    .join('|')
}

// Helper to parse ISO date as local date
function parseLocalDate(isoDateStr: string) {
  const [year, month, day] = isoDateStr.split('-').map(Number)
  return new Date(year, month - 1, day)
}

const AdminItemsPage = () => {
  const { radarConfig, radarItems, currentRadarId } = useSnapshot(state)
  const navigate = useNavigate()
  const config = radarConfig ? { ...radarConfig } : null
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedRelease, setSelectedRelease] = useState<string>('all')

  const items = [...(radarItems || [])] as Item[]

  // Generate unique key for the table based on items data
  const tableKey = useMemo(() => calculateItemsHash(items), [items])

  const [licenseInfo, setLicenseInfo] = useState<LicenseCheckResponse | null>(
    null
  )

  // Extract all unique release dates from items
  const extractAllReleaseDates = useCallback((items: Item[]): string[] => {
    // Create a Set to store unique release dates
    const uniqueReleaseDates = new Set<string>()

    // Iterate through all items and their releases
    for (const item of items) {
      for (const release of item.releases) {
        if (release.releaseDate) {
          uniqueReleaseDates.add(release.releaseDate)
        }
      }
    }

    // Convert Set to array and sort by date (newest first)
    return Array.from(uniqueReleaseDates).sort((a, b) => {
      return parseLocalDate(b).getTime() - parseLocalDate(a).getTime()
    })
  }, [])

  // Get all unique release dates from the current items
  const allReleaseDates = useMemo(() => {
    return extractAllReleaseDates(items)
  }, [items, extractAllReleaseDates])

  // Reset selectedRelease if the current selection is no longer available
  useEffect(() => {
    if (selectedRelease === 'all') {
      return
    }
    if (!allReleaseDates.includes(selectedRelease)) {
      setSelectedRelease('all')
    }
  }, [allReleaseDates, selectedRelease])

  // Add styles for dropdowns
  useEffect(() => {
    // Add the CSS for dropdowns to the document head
    const styleElement = document.createElement('style')
    styleElement.textContent = `
      .dropdown-content {
        --dropdown-bg: #ffffff;
        --dropdown-text: #172B4D;
        --dropdown-border: #DFE1E6;
        --dropdown-hover: #F4F5F7;
        --dropdown-selected: #EBECF0;
        --dropdown-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
      }
    
      @media (prefers-color-scheme: dark) {
        .dropdown-content {
          --dropdown-bg: #282E33;
          --dropdown-text: #FFFFFF;
          --dropdown-border: #505050;
          --dropdown-hover: #3E474F;
          --dropdown-selected: #353D45;
          --dropdown-shadow: 0 4px 8px rgba(0, 0, 0, 0.5);
        }
      }
    
      .dropdown-content {
        background-color: var(--dropdown-bg) !important;
        color: var(--dropdown-text) !important;
        border: 1px solid var(--dropdown-border) !important;
        box-shadow: var(--dropdown-shadow) !important;
      }
    
      .dropdown-item {
        background-color: var(--dropdown-bg) !important;
        color: var(--dropdown-text) !important;
        transition: background-color 0.2s ease;
      }
    
      .dropdown-item:hover {
        background-color: var(--dropdown-hover) !important;
      }
    
      .dropdown-item[data-selected=true] {
        background-color: var(--dropdown-selected) !important;
      }

      /* Table cell border fix */
      .items-table-custom .nextui-table-cell,
      .items-table-custom td,
      .items-table-custom tr td {
        border-color: var(--ds-border) !important;
        border-bottom-color: var(--ds-border) !important;
        outline: none !important;
      }

      .items-table-custom .nextui-table-cell:focus,
      .items-table-custom .nextui-table-cell:active,
      .items-table-custom td:focus,
      .items-table-custom td:active,
      .items-table-custom tr:focus td,
      .items-table-custom tr:active td,
      .items-table-custom tr td:focus,
      .items-table-custom tr td:active {
        border-color: var(--ds-border) !important;
        border-bottom-color: var(--ds-border) !important;
        outline: none !important;
        box-shadow: none !important;
      }
    `
    document.head.appendChild(styleElement)

    // Cleanup function to remove the style element when component unmounts
    return () => {
      document.head.removeChild(styleElement)
    }
  }, []) // Empty dependency array means this runs once on mount

  useEffect(() => {
    const checkLicense = async () => {
      if (currentRadarId) {
        const info = await api.checkLicenseLimit(currentRadarId)
        setLicenseInfo(info)
      }
    }
    checkLicense()
  }, [currentRadarId])

  const filteredItems = items.filter(item => {
    const latestRelease = getLatestRelease(item)
    const matchesSearch =
      latestRelease.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      latestRelease.body.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesRelease =
      selectedRelease === 'all' || latestRelease.releaseDate === selectedRelease
    return matchesSearch && matchesRelease
  })

  // Generate release dropdown options from the unique release dates
  const releaseOptions = [
    { key: 'all', value: 'all', label: 'All Releases' },
    ...allReleaseDates.map(release => ({
      key: release,
      value: release,
      label: release
    }))
  ]

  // Helper function to get display names and colors
  const getQuadrantTitle = (id: string) => {
    const quadrant = config?.quadrants.find(q => q.id === id)
    return {
      title: quadrant?.title || id,
      color: quadrant?.color
    }
  }

  const getRingTitle = (id: string) => {
    const ring = config?.rings.find(r => r.id === id)
    return {
      title: ring?.title || id,
      color: ring?.color
    }
  }

  const onEditItem = (item: Item) => {
    navigate(`/admin/edit-item/${item.id}`)
  }

  return (
    <>
      <div className="space-y-4 my-6" data-testid="items-table-container">
        {/* Filters */}
        <div
          className="flex flex-wrap items-center justify-between gap-3"
          data-testid="items-table-filters"
        >
          <div className="flex flex-wrap gap-3">
            <Input
              variant="flat"
              isClearable
              onClear={() => setSearchQuery('')}
              classNames={{
                base: 'w-80',
                input:
                  'text-sm group-data-[has-value=true]:text-[var(--ds-text)] outline-none',
                inputWrapper:
                  'border bg-[var(--ds-surface)] data-[hover=true]:bg-[var(--ds-surface)] focus-within:bg-[var(--ds-surface)] rounded-[3px] h-8 data-[focused=true]:border-[var(--ds-border-focused)]',
                clearButton:
                  'px-2 pt-1 transition-opacity opacity-0 data-[visible=true]:opacity-100 text-[var(--ds-text-subtlest)]'
              }}
              placeholder="Search items..."
              size="sm"
              startContent={
                <MagnifyingGlassIcon
                  className="text-[var(--ds-text-subtlest)] w-5 h-5"
                  aria-hidden="true"
                  data-testid="search-icon"
                />
              }
              type="search"
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearchQuery(e.target.value)
              }
              data-testid="items-search-input"
              aria-label="Search radar items"
            />
            <Select
              aria-label="Release selection"
              selectedKeys={selectedRelease ? [selectedRelease] : []}
              selectionMode="single"
              onSelectionChange={keys => {
                const key = Array.from(keys)[0]?.toString() || 'all'
                setSelectedRelease(key)
              }}
              variant="flat"
              classNames={{
                base: 'bg-transparent rounded-sm',
                trigger: `h-7 min-h-0 px-2 py-0.5 text-black dark:text-white bg-transparent border border-[var(--ds-border)] hover:border-blue-500 hover:!bg-transparent data-[hover=true]:!bg-transparent items-center transition-none`,
                value: 'truncate',
                listbox:
                  'bg-[var(--ds-surface)] shadow-lg',
                popoverContent: 'bg-[var(--ds-surface)]'
              }}
              style={{ color: 'var(--ds-text)', borderWidth: '1px' }}
              renderValue={selected => {
                const key = selected?.[0]
                const option = releaseOptions.find(opt => opt.value === key)
                return (
                  <span style={{ color: 'var(--ds-text)' }}>
                    {option?.label ||
                      releaseOptions.find(opt => opt.value === selectedRelease)
                        ?.label ||
                      'All Releases'}
                  </span>
                )
              }}
              placeholder={
                releaseOptions.find(opt => opt.value === selectedRelease)
                  ?.label || 'All Releases'
              }
              data-testid="release-dropdown-trigger"
              fullWidth={false}
              radius="sm"
              // No endContent, use default select arrow
            >
              {releaseOptions.map(option => (
                <SelectItem
                  key={option.value}
                  className="dropdown-item"
                  classNames={{ selectedIcon: 'flex items-center' }}
                  data-testid={`release-option-${option.value}`}
                >
                  {option.label}
                </SelectItem>
              ))}
            </Select>
          </div>
          <div className="flex items-center gap-3">
            {licenseInfo && !licenseInfo.canAdd && (
              <div
                className="text-sm text-[var(--ds-text-danger)] whitespace-normal max-w-[400px]"
                data-testid="license-limit-warning"
                role="alert"
              >
                You've reached the limit of {licenseInfo.maxItems} radar items
                in the unlicensed version. Please{' '}
                <button
                  type="button"
                  className="underline p-0 cursor-pointer"
                  onClick={() => navigate('/admin/billing')}
                  data-testid="upgrade-button"
                >
                  upgrade
                </button>{' '}
                or delete items.
              </div>
            )}
            <Button
              variant="solid"
              size="sm"
              className="bg-primary text-white font-bold rounded-[3px] h-9 px-3"
              startContent={<PlusIcon className="w-4 h-4" />}
              onPress={() => navigate('/admin/new-item')}
              isDisabled={!!(licenseInfo && !licenseInfo.canAdd)}
              disableRipple
              data-testid="add-radar-item-button"
            >
              Add Radar Item
            </Button>
          </div>
        </div>

        {/* Items Table */}
        <Card
          className="border border-[var(--ds-border)] bg-[var(--ds-surface)] rounded-[3px] shadow-none"
          data-testid="items-table-card"
        >
          <Table
            key={tableKey}
            aria-label="Tech Radar Items"
            classNames={{
              wrapper:
                'bg-transparent rounded-[3px] overflow-hidden items-table-custom',
              base: 'rounded-[3px] overflow-hidden',
              th: 'bg-[var(--ds-background-neutral)] text-[var(--ds-text-subtle)] h-8 admin-items-header-cell',
              td: 'text-[var(--ds-text)] border-b border-[var(--ds-border)] h-12 focus:outline-none active:outline-none'
            }}
            selectionMode="none"
            data-testid="items-table"
            className="items-table-custom"
          >
            <TableHeader>
              <TableColumn
                className="w-[25%] !rounded-[3px] bg-[var(--ds-surface-raised)] font-bold text-[var(--ds-text)] border-b-2 border-[var(--ds-border)]"
                data-testid="column-title"
              >
                Title
              </TableColumn>
              <TableColumn
                className="w-[25%] bg-[var(--ds-surface-raised)] font-bold text-[var(--ds-text)] border-b-2 border-[var(--ds-border)]"
                data-testid="column-quadrant"
              >
                Quadrant
              </TableColumn>
              <TableColumn
                className="w-[15%] bg-[var(--ds-surface-raised)] font-bold text-[var(--ds-text)] border-b-2 border-[var(--ds-border)]"
                data-testid="column-ring"
              >
                Ring
              </TableColumn>
              <TableColumn
                className="w-[15%] bg-[var(--ds-surface-raised)] font-bold text-[var(--ds-text)] border-b-2 border-[var(--ds-border)]"
                data-testid="column-release"
              >
                Release
              </TableColumn>
              <TableColumn
                className="w-[10%] bg-[var(--ds-surface-raised)] font-bold text-[var(--ds-text)] border-b-2 border-[var(--ds-border)]"
                data-testid="column-status"
              >
                Status
              </TableColumn>
              <TableColumn
                className="w-[10%] !rounded-[3px] bg-[var(--ds-surface-raised)] font-bold text-[var(--ds-text)] border-b-2 border-[var(--ds-border)]"
                data-testid="column-archived"
              >
                Archived
              </TableColumn>
            </TableHeader>
            <TableBody emptyContent="Add a Radar Item to get started">
              {filteredItems.map(item => {
                const latestRelease = getLatestRelease(item)
                return (
                  <TableRow
                    key={item.id}
                    className="hover:bg-[var(--ds-background-neutral-subtle-hovered)] cursor-pointer"
                    onClick={() => onEditItem(item)}
                    onKeyDown={(e: React.KeyboardEvent) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        onEditItem(item)
                      }
                    }}
                    tabIndex={0}
                    data-testid={`item-row-${item.id}`}
                    aria-label={`Edit ${latestRelease.title}`}
                  >
                    <TableCell>
                      <div
                        className="text-[var(--ds-text)]"
                        data-testid={`item-title-${item.id}`}
                      >
                        {latestRelease.title || item.id}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div
                        className="flex items-center"
                        data-testid={`item-quadrant-${item.id}`}
                      >
                        <div
                          className="w-1 h-5 rounded-xs mr-2"
                          style={{
                            backgroundColor: getQuadrantTitle(item.quadrant)
                              .color
                          }}
                          aria-hidden="true"
                        />
                        {getQuadrantTitle(item.quadrant).title}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div
                        className="flex items-center"
                        data-testid={`item-ring-${item.id}`}
                      >
                        <div
                          className="w-1 h-5 rounded-xs mr-2"
                          style={{
                            backgroundColor: getRingTitle(latestRelease.ring)
                              .color
                          }}
                          aria-hidden="true"
                        />
                        {getRingTitle(latestRelease.ring).title}
                      </div>
                    </TableCell>
                    <TableCell data-testid={`item-release-${item.id}`}>
                      {latestRelease.releaseDate}
                    </TableCell>
                    <TableCell>
                      <Chip
                        className="capitalize rounded-none"
                        size="sm"
                        variant="flat"
                        style={{
                          backgroundColor:
                            config &&
                            latestRelease.flag &&
                            config.flags[
                              latestRelease.flag as keyof typeof config.flags
                            ]
                              ? `${config.flags[latestRelease.flag as keyof typeof config.flags].color}20`
                              : undefined,
                          color:
                            config &&
                            latestRelease.flag &&
                            config.flags[
                              latestRelease.flag as keyof typeof config.flags
                            ]
                              ? config.flags[
                                  latestRelease.flag as keyof typeof config.flags
                                ].color
                              : undefined
                        }}
                        data-testid={`item-status-${item.id}`}
                      >
                        {config &&
                        latestRelease.flag &&
                        config.flags[
                          latestRelease.flag as keyof typeof config.flags
                        ]
                          ? config.flags[
                              latestRelease.flag as keyof typeof config.flags
                            ].title
                          : latestRelease.flag}
                      </Chip>
                    </TableCell>
                    <TableCell data-testid={`item-archived-cell-${item.id}`}>
                      {item.archived && (
                        <Chip
                          className="rounded-none"
                          size="sm"
                          variant="flat"
                          style={{
                            backgroundColor:
                              'var(--ds-background-neutral-subtle)',
                            color: 'var(--ds-text-subtle)'
                          }}
                          data-testid={`item-archived-${item.id}`}
                        >
                          Archived
                        </Chip>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </Card>
      </div>
    </>
  )
}

export default AdminItemsPage
